// src/components/dashboards/composition/smartText/servedSentence.ts
//
// Which tiles carry a sentence the SERVER writes per request (plan
// 2026-10-16-a-block-subtitle, V1(b)/V3): a smart text tile, and a chart or
// table tile whose subtitle declares variables. Both get their text only from
// the single-item refresh (`RefreshItemResponse.smart_text` / `.subtitle`),
// and both name a period, so neither may be served by the bulk call, the
// browser cache or a hydrated snapshot, and both carry the period preset in
// their view key. ONE predicate, `needsServedSentence`, decides that at every
// routing site (`Insights.tsx`, `dashboardCache.ts`, `useDashboardPrefetch.ts`),
// so the two kinds cannot drift apart. A plain-text subtitle (no variables)
// changes nothing in routing.
//
// `isSmartTextItem` stays the name for the smart text tile itself (which
// tile component draws it).
import { blockTypeOfSpec } from "../blocks";
import {
  SMART_TEXT_LANGUAGES,
  isSmartTextItem,
  variablesOf,
  type SmartTextLanguage,
  type SmartTextVariables,
} from "./smartTextModel";

/** `services/composition/block_subtitle.py::SUBTITLE_MAX_CHARS`. */
export const SUBTITLE_MAX_LENGTH = 200;
/** `block_subtitle.py::SUBTITLE_MAX_VARIABLES`. */
export const SUBTITLE_MAX_VARIABLES = 3;

type SubtitleCarrier = {
  chart_spec_json?: unknown;
  subtitle?: string | null;
  subtitle_smart_text?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Only chart and table blocks draw a subtitle (`SUBTITLE_BLOCK_TYPES`). */
export function drawsSubtitle(item: SubtitleCarrier | null | undefined): boolean {
  const type = blockTypeOfSpec(asRecord(item?.chart_spec_json));
  return type === "chart" || type === "table";
}

/** The subtitle a tile draws, or null: none stored, blank, or a block that
 *  draws no subtitle. */
export function subtitleOf(item: SubtitleCarrier | null | undefined): string | null {
  const text = typeof item?.subtitle === "string" ? item.subtitle : "";
  return text.trim() !== "" && drawsSubtitle(item) ? text : null;
}

/** The subtitle's declared variables, normalised (empty for a plain one). */
export function subtitleVariablesOf(item: SubtitleCarrier | null | undefined): SmartTextVariables {
  return variablesOf(asRecord(item?.subtitle_smart_text));
}

export function subtitleLanguageOf(item: SubtitleCarrier | null | undefined): SmartTextLanguage {
  const language = asRecord(item?.subtitle_smart_text)?.language;
  return (SMART_TEXT_LANGUAGES as readonly unknown[]).includes(language) ? (language as SmartTextLanguage) : "en";
}

/** A chart/table tile whose subtitle declares variables: its numbers come
 *  with the single refresh. */
export function hasSubtitleVariables(item: SubtitleCarrier | null | undefined): boolean {
  return subtitleOf(item) !== null && Object.keys(subtitleVariablesOf(item)).length > 0;
}

/** The routing predicate: a smart text tile OR a subtitle with variables. */
export function needsServedSentence(item: SubtitleCarrier | null | undefined): boolean {
  return isSmartTextItem(item) || hasSubtitleVariables(item);
}
